import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import { isProfessionalDocumentsDisabled } from '@/utils/qualificationErrors'
import { QUEUE_LOAD_ERROR } from '@/modules/company/Approvals/ProfessionalDocuments/constants'
import type {
  ApproveVersionPayload,
  BulkDecisionResponse,
  ProfessionalDocumentStatus,
  ProfessionalDocumentsDetailResponse,
  RejectVersionPayload,
  ReviewQueueParams,
  ReviewQueueProfessional,
  ReviewQueueTag
} from '@/types/professional-documents.types'

export interface ReviewQueueFilters {
  search: string
  tag: ReviewQueueTag | null
  status: ProfessionalDocumentStatus | null
  typeKey: string | null
}

export const EMPTY_REVIEW_FILTERS: ReviewQueueFilters = {
  search: '',
  tag: null,
  status: null,
  typeKey: null
}

/** `userId` = `User.id` do profissional (Adendo A8): é o detalhe que recarrega depois da decisão. */
export interface DecisionTarget {
  versionId: number
  userId: number
}

export type ApproveTarget = DecisionTarget & { payload: ApproveVersionPayload }

/** Página única: a fila é por médico e a tela dividida navega em sequência (backend: máx. 100). */
const QUEUE_LIMIT = 50
/** Teto do backend por chamada de lote (`BULK_DECISION_MAX_ITEMS`); acima disso, 400. */
const BULK_LIMIT = 50

function toQueueParams(filters: ReviewQueueFilters): ReviewQueueParams {
  const params: ReviewQueueParams = { page: 1, limit: QUEUE_LIMIT }
  if (filters.search.trim()) params.search = filters.search.trim()
  if (filters.tag) params.tag = filters.tag
  if (filters.status) params.status = filters.status
  if (filters.typeKey) params.typeKey = filters.typeKey
  return params
}

/** Lotes de até `BULK_LIMIT`, em sequência; o resultado parcial de cada um é somado. */
async function decideInBatches<T>(
  targets: T[],
  send: (batch: T[]) => Promise<BulkDecisionResponse>
): Promise<BulkDecisionResponse> {
  const result: BulkDecisionResponse = { succeeded: [], failed: [] }
  for (let start = 0; start < targets.length; start += BULK_LIMIT) {
    const partial = await send(targets.slice(start, start + BULK_LIMIT))
    result.succeeded.push(...partial.succeeded)
    result.failed.push(...partial.failed)
  }
  return result
}

export const useDocumentReviewStore = defineStore('documentReview', () => {
  const items = ref<ReviewQueueProfessional[]>([])
  const total = ref(0)
  const filters = ref<ReviewQueueFilters>({ ...EMPTY_REVIEW_FILTERS })
  const isLoading = ref(false)
  /** Backend com a flag desligada (404 `PROFESSIONAL_DOCUMENTS_DISABLED`). */
  const isUnavailable = ref(false)
  const loadError = ref<string | null>(null)
  const hasError = computed(() => loadError.value !== null)
  const details = ref<Record<number, ProfessionalDocumentsDetailResponse>>({})
  const loadingDetailFor = ref<number | null>(null)
  const isSaving = ref(false)
  let fetchId = 0

  const hasActiveFilters = computed(() => {
    const f = filters.value
    return Boolean(f.search.trim() || f.tag || f.status || f.typeKey)
  })

  async function fetchQueue(options: { silent?: boolean } = {}): Promise<void> {
    const current = ++fetchId
    if (!options.silent) isLoading.value = true
    loadError.value = null
    try {
      const response = await Network.professionalDocuments.listReviewQueue(
        toQueueParams(filters.value)
      )
      if (current !== fetchId) return
      items.value = response.items
      total.value = response.meta.total
      isUnavailable.value = false
    } catch (error) {
      if (current !== fetchId) return
      if (isProfessionalDocumentsDisabled(error)) {
        // fallback intencional: a feature está desligada no backend; `isUnavailable` é o
        // estado que a tela mostra ("indisponível"), não é erro nem toast.
        isUnavailable.value = true
        return
      }
      console.error(error)
      loadError.value = getApiErrorMessage(error, QUEUE_LOAD_ERROR)
    } finally {
      if (current === fetchId) isLoading.value = false
    }
  }

  function setFilters(next: ReviewQueueFilters): Promise<void> {
    filters.value = { ...next }
    return fetchQueue()
  }

  /** Relança: quem abre o detalhe decide entre `notifyError` e o estado "indisponível". */
  async function fetchProfessional(
    userId: number,
    force = false
  ): Promise<ProfessionalDocumentsDetailResponse> {
    const cached = details.value[userId]
    if (cached && !force) return cached
    loadingDetailFor.value = userId
    try {
      const detail = await Network.professionalDocuments.getProfessional(userId)
      details.value = { ...details.value, [userId]: detail }
      return detail
    } finally {
      if (loadingDetailFor.value === userId) loadingDetailFor.value = null
    }
  }

  /** Depois de qualquer decisão (mesmo com erro): fila e detalhes voltam a refletir o backend. */
  async function refreshAfterDecision(userIds: number[]): Promise<void> {
    const unique = [...new Set(userIds)]
    await Promise.allSettled([
      fetchQueue({ silent: true }),
      ...unique.map((id) => fetchProfessional(id, true))
    ])
  }

  /** Relança o erro (ex.: 409 `DOCUMENT_NOT_IN_REVIEW`, decisão concorrente) depois do refetch. */
  async function approve(targets: ApproveTarget[]): Promise<BulkDecisionResponse> {
    isSaving.value = true
    try {
      if (targets.length === 1) {
        const [target] = targets
        await Network.professionalDocuments.approve(target.versionId, target.payload)
        return { succeeded: [target.versionId], failed: [] }
      }
      return await decideInBatches(targets, (batch) =>
        Network.professionalDocuments.bulkApprove({
          items: batch.map((target) => ({ versionId: target.versionId, ...target.payload }))
        })
      )
    } finally {
      isSaving.value = false
      await refreshAfterDecision(targets.map((target) => target.userId))
    }
  }

  async function reject(
    targets: DecisionTarget[],
    payload: RejectVersionPayload
  ): Promise<BulkDecisionResponse> {
    isSaving.value = true
    try {
      if (targets.length === 1) {
        const [target] = targets
        await Network.professionalDocuments.reject(target.versionId, payload)
        return { succeeded: [target.versionId], failed: [] }
      }
      return await decideInBatches(targets, (batch) =>
        Network.professionalDocuments.bulkReject({
          versionIds: batch.map((target) => target.versionId),
          ...payload
        })
      )
    } finally {
      isSaving.value = false
      await refreshAfterDecision(targets.map((target) => target.userId))
    }
  }

  async function undo(target: DecisionTarget): Promise<void> {
    isSaving.value = true
    try {
      await Network.professionalDocuments.undo(target.versionId)
    } finally {
      isSaving.value = false
      await refreshAfterDecision([target.userId])
    }
  }

  function $reset() {
    fetchId++
    items.value = []
    total.value = 0
    filters.value = { ...EMPTY_REVIEW_FILTERS }
    isLoading.value = false
    isUnavailable.value = false
    loadError.value = null
    details.value = {}
    loadingDetailFor.value = null
    isSaving.value = false
  }

  return {
    items,
    total,
    filters,
    isLoading,
    isUnavailable,
    loadError,
    hasError,
    hasActiveFilters,
    details,
    loadingDetailFor,
    isSaving,
    fetchQueue,
    setFilters,
    fetchProfessional,
    approve,
    reject,
    undo,
    $reset
  }
})
